import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';
import { environment } from '@environments/environment';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { dialogErrorConfig } from '@shared/constants/dialog-error-constant';
import { AuthGuardService } from '@shared/services/auth-guard.service';
import { LayoutActionErrorUpdate } from '@shared/store/actions/layout.action';
import { selectLayoutState } from '@shared/store/selectors/layout-selector';
import { AppStates } from '@shared/store/state/app.state';
import { ConfirmationService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, Subscription, takeUntil } from 'rxjs';

import { environment as env } from '../environments/environment';
import { LayoutService } from './layout/service/app.layout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, ConfirmDialogModule]
})
export class AppComponent implements OnInit, OnDestroy {
  isGeneralError: Subscription;
  isAlreadyDisplayError: boolean;
  ref: DynamicDialogRef | undefined;
  onComponentDestroy$ = new Subject<void>();
  title = '';

  constructor(
    private readonly store: Store<AppStates>,
    private translate: TranslateService,
    public dialogService: DialogService,
    public confirmationService: ConfirmationService,
    private config: PrimeNG,
    protected authGuardService: AuthGuardService,
    private readonly router: Router,
    private readonly titleService: Title,
    private layoutService: LayoutService,
    private renderer2: Renderer2
  ) {
    this.translate.setDefaultLang('th');
    this.translate.use('th');
  }

  ngOnInit(): void {
    this.initialize();
    this.apiError();
    this.config.setTranslation({
      monthNames: [
        'มกราคม',
        'กุมภาพันธ์',
        'มีนาคม',
        'เมษายน',
        'พฤษภาคม',
        'มิถุนายน',
        'กรกฎาคม',
        'สิงหาคม',
        'กันยายน',
        'ตุลาคม',
        'พฤศจิกายน',
        'ธันวาคม'
      ],
      monthNamesShort: [
        'ม.ค.',
        'ก.พ.',
        'มี.ค.',
        'เม.ย.',
        'พ.ค.',
        'มิ.ย.',
        'ก.ค.',
        'ส.ค.',
        'ก.ย.',
        'ต.ค.',
        'พ.ย.',
        'ธ.ค.'
      ],
      dayNamesMin: ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
    });

    this.setTitle();
  }

  setTitle() {
    const title = `${environment.appTitle} ${environment.tier !== 'prod' ? ' - ' + environment.tier.toUpperCase() : ''}`;
    console.log('environment.tier ', environment.tier);
    this.titleService.setTitle(title);
  }

  ngOnDestroy(): void {
    this.onComponentDestroy$.next();
    this.isGeneralError.unsubscribe();
    this.store.dispatch(new LayoutActionErrorUpdate(false));
  }

  apiError() {
    this.isGeneralError = this.store
      .pipe(select(selectLayoutState), takeUntil(this.onComponentDestroy$))
      .subscribe((layoutError) => {
        let initialState = null;
        if (layoutError.isApiError && !this.isAlreadyDisplayError) {
          if (
            layoutError.isUseBackEndMessage &&
            layoutError.errorResponse.error &&
            layoutError.errorResponse.error.message
          ) {
            this.isAlreadyDisplayError = true;
            initialState = {
              ...dialogErrorConfig,
              header: 'ทำรายการไม่สำเร็จ',
              message: `
              <div class="my-3">${layoutError.errorResponse.error.message}</div>
            `,
              accept: () => {
                this.onCloseModal();
              }
            };
          } else {
            this.isAlreadyDisplayError = true;
            initialState = {
              ...dialogErrorConfig,
              header: 'ทำรายการไม่สำเร็จ',
              message: `
                  <div class="my-3">ไม่สามารถดำเนินการได้ในขณะนี้ กรุณาลองใหม่อีกครั้งภายหลัง</div>
                `,
              accept: () => {
                this.onCloseModal();
              }
            };
          }
        } else if (!layoutError.isApiError) {
          this.isAlreadyDisplayError = false;
        }

        if (initialState) {
          this.confirmationService.confirm({ ...initialState });
        }
      });
  }

  onCloseModal() {
    this.isAlreadyDisplayError = false;
    this.store.dispatch(new LayoutActionErrorUpdate(false));
  }

  initialize() {
    this.layoutService.setTheme(env.theme, this.renderer2);
  }
}
